import { useEffect, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { Link } from 'react-router-dom';
import { db } from '../db/db';
import { useAuth } from '../auth/AuthContext';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { ProgressBar } from '../components/ProgressBar';
import { syncAssignedJobs } from '../sync/jobPullSync';
import { pullJobStatus } from '../sync/progressPullSync';
import { flushPendingProgress } from '../sync/progressSync';

export function JobsPage() {
  const { session, signOut } = useAuth();
  const online = useOnlineStatus();
  const jobs = useLiveQuery(() => db.jobs.orderBy('shootDate').reverse().toArray(), []);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState<string | null>(null);

  async function runSync() {
    setSyncing(true);
    // Retry any of this device's own marks that didn't make it up yet
    // (e.g. made while offline) before pulling — see
    // useJobStatusSync.ts for why this can't be a one-shot attempt.
    await flushPendingProgress();
    const result = await syncAssignedJobs();
    // Also refresh progress on jobs already local — "Refresh jobs" is the
    // one manual sync action a photographer has, so it should catch up on
    // what other photographers on the same job have marked too, not just
    // pull brand-new assignments.
    const localJobs = await db.jobs.toArray();
    await Promise.all(localJobs.map(j => pullJobStatus(j.id)));
    setSyncing(false);
    if (result.error) setSyncStatus(result.error);
    else if (result.pulled.length > 0) {
      setSyncStatus(`Added ${result.pulled.length} new job${result.pulled.length === 1 ? '' : 's'}.`);
    } else {
      setSyncStatus(null);
    }
  }

  useEffect(() => {
    if (session?.role === 'photographer') void runSync();
  }, [session?.userId]);

  const todayIso = new Date().toISOString().slice(0, 10);
  const sorted = (jobs ?? []).slice().sort((a, b) => {
    const aToday = a.shootDate === todayIso ? 0 : 1;
    const bToday = b.shootDate === todayIso ? 0 : 1;
    if (aToday !== bToday) return aToday - bToday;
    return b.shootDate.localeCompare(a.shootDate);
  });

  return (
    <div className="screen">
      <header className="app-header">
        <div>
          <h1>Your jobs</h1>
          {session && <p className="app-header-sub">Signed in as {session.displayName}</p>}
        </div>
        <div className="app-header-actions">
          <span className={`online-pill ${online ? 'online-pill--online' : 'online-pill--offline'}`}>
            {online ? 'Online' : 'Offline'}
          </span>
          {session?.role === 'admin' && (
            <Link to="/admin" className="btn btn--ghost">
              Admin
            </Link>
          )}
          {session?.role === 'photographer' && (
            <button className="btn btn--ghost" onClick={runSync} disabled={syncing}>
              {syncing ? 'Checking…' : 'Refresh jobs'}
            </button>
          )}
          <button className="btn btn--ghost" onClick={signOut}>
            Sign out
          </button>
        </div>
      </header>

      {syncStatus && <p className="sync-status">{syncStatus}</p>}

      {sorted.length === 0 && (
        <div className="empty-state">
          <p>No jobs yet.</p>
          <Link to="/import" className="btn btn--primary">
            Import your first job
          </Link>
        </div>
      )}

      <ul className="job-list">
        {sorted.map(job => (
          <li key={job.id}>
            <Link
              to={job.importStatus === 'confirmed' ? `/jobs/${job.id}` : `/import/review/${job.id}`}
              className="job-card"
            >
              <div className="job-card-main">
                <h2>{job.name}</h2>
                <p className="job-card-meta">
                  {job.schoolLabel} · {job.shootDate}
                </p>
                {job.importStatus === 'confirmed' ? (
                  <ProgressBar completed={job.completedSubjects} total={job.totalSubjects} />
                ) : (
                  <span className="badge badge--warning">Import in review</span>
                )}
              </div>
              <div className="job-card-status">
                {job.availableOffline ? (
                  <span className="badge badge--success">Available offline</span>
                ) : (
                  <span className="badge">Not downloaded</span>
                )}
              </div>
            </Link>
          </li>
        ))}
      </ul>

      {/* Admin/import is a distinct, secondary action — deliberately kept
          off the fast photographer path (spec: "Admin/import action is
          separated from the fast photographer workflow"). */}
      <Link to="/import" className="fab" aria-label="Import a job">
        +
      </Link>
    </div>
  );
}
